import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  User,
  Food,
  FoodLogEntry,
  BloodworkEntry,
  Analysis,
  RegisterData,
  LoginData,
  ApiResponse,
  PaginatedResponse,
  NutritionSummary,
  LabValue,
} from '../types';

// Use current origin in production, localhost in development
export const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? `${window.location.origin}/api` : `${window.location.hostname === 'localhost' ? 'http://localhost:5000' : `http://${window.location.hostname}:5000`}/api`);

// Simple in-memory cache for API responses
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiry: number;
}

class APICache {
  private cache = new Map<string, CacheEntry<any>>();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

  set<T>(key: string, data: T, ttl: number = this.DEFAULT_TTL): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      expiry: Date.now() + ttl
    });
  }

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    
    if (Date.now() > entry.expiry) {
      this.cache.delete(key);
      return null;
    }
    
    return entry.data as T;
  }

  clear(): void {
    this.cache.clear();
  }

  // Delete a specific cache entry
  delete(key: string): void {
    this.cache.delete(key);
  }

  // Clear cache entries older than expiry time
  cleanup(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];
    this.cache.forEach((entry, key) => {
      if (now > entry.expiry) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.cache.delete(key));
  }
}

const apiCache = new APICache();

// Expose cache globally for clearing on logout
if (typeof window !== 'undefined') {
  (window as any).apiCache = apiCache;
}

// Cleanup cache every 10 minutes
setInterval(() => apiCache.cleanup(), 10 * 60 * 1000);

// ---------------------------------------------------------------------------
// "What changed" (Analysis page strip) — GET /api/analysis/what-changed
// ---------------------------------------------------------------------------

/** Which Analysis tile an item drills into (null = no drilldown). */
export type WhatChangedTile =
  | 'calories' | 'micro' | 'fiber' | 'heart' | 'recovery' | 'sleep'
  | 'cico' | 'hrv' | 'mass' | 'radar' | 'build';

export interface WhatChangedItem {
  key: string;
  title: string;
  detail: string;
  value: number;
  prior: number;
  /** 'kcal' | 'g' | 'kg' | 'ms' | 'bpm' | 'steps' | 'min' | 'workouts' | '%' | 'days' … — weight is always kg. */
  unit: string;
  changePct: number | null;
  direction: 'up' | 'down' | 'flat';
  /** true = good news, false = worth a look, null = neutral. */
  good: boolean | null;
  tile: WhatChangedTile | null;
  magnitude: number;
}

export interface WhatChangedData {
  days: number;
  periodStart: string;
  periodEnd: string;
  priorStart: string;
  priorEnd: string;
  reason?: 'not_enough_data';
  /** Already ranked by the server, at most 4. */
  items: WhatChangedItem[];
  skipped: string[];
}

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Every account-creating request carries where the visit came from (utils/attribution.ts).
    this.api.interceptors.request.use((config) => {
      try {
        if (config.method === 'post' && /\/auth\/(register|google|apple)$/.test(config.url || '')) {
          // eslint-disable-next-line @typescript-eslint/no-var-requires
          const { attributionForSignup } = require('../utils/attribution');
          config.data = { ...(config.data || {}), attribution: attributionForSignup() };
        }
      } catch { /* never block auth */ }
      return config;
    });

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        const url: string = error.config?.url || '';
        // Auth endpoints return 401 as an expected inline error (bad password,
        // invalid token) — don't treat those as a session expiry.
        const isAuthEndpoint = /\/auth\/(login|register|google|apple|reset-password|forgot-password|resend-verification|verify)/.test(url);
        if (error.response?.status === 401 && !isAuthEndpoint) {
          // Session expired/invalid. Dispatch an event so AuthContext can log out
          // through React state + Router (no full-page reload that would destroy
          // in-progress chat drafts, scan results, or food edits).
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.dispatchEvent(new CustomEvent('auth:unauthorized'));
        }
        return Promise.reject(error);
      }
    );
  }

  setToken(token: string | null) {
    if (token) {
      this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete this.api.defaults.headers.common['Authorization'];
    }
  }

  // Auth endpoints
  async login(data: LoginData): Promise<{ user: User; token: string }> {
    const response = await this.api.post('/auth/login', data);
    return response.data; // Backend returns data directly
  }

  async register(data: RegisterData): Promise<{ token: string; user: User }> {
    const res = await this.api.post('/auth/register', data);
    return res.data;
  }

  async getProfile(): Promise<any> {
    const response = await this.api.get('/auth/me');
    return response.data.user; // Backend returns { success: true, user: {...} }
  }

  async updateProfile(data: Partial<any>): Promise<any> {
    const response = await this.api.put('/auth/profile', data);
    return response.data.user; // Backend returns { success: true, user: {...} }
  }

  // Exact nutrients (USDA FoodData Central) — utils/usda.ts
  async usdaSearch(q: string): Promise<{ available: boolean; generic: any[]; branded: any[] }> {
    const response = await this.api.get('/usda/search', { params: { q } });
    return response.data;
  }

  async usdaFood(fdcId: number): Promise<any> {
    const response = await this.api.get(`/usda/food/${fdcId}`);
    return response.data.food;
  }

  async usdaMatch(foods: Array<{ name: string }>): Promise<Array<any | null>> {
    const response = await this.api.post('/usda/match', { foods });
    return response.data.matches || [];
  }

  async saveHeardFrom(choice: string, detail?: string): Promise<void> {
    await this.api.post('/attribution/heard-from', { choice, detail });
  }

  async getActivationStatus(): Promise<{
    success: boolean;
    steps: { meal: boolean; supplements: boolean; bloodwork: boolean; profile: boolean; sync: boolean; workout: boolean };
    completedCount: number;
    totalSteps: number;
    graduated: boolean;
    graduatedByCompletion: boolean;
    graduatedByAge: boolean;
    freeProDayExpiresAt: string | null;
    isFreeProDay: boolean;
    foodDays?: number;
    tier: string;
  }> {
    const response = await this.api.get('/onboarding/activation-status');
    return response.data;
  }

  async uploadProfilePicture(file: File): Promise<{ profilePicture: { url: string; publicId: string } }> {
    const formData = new FormData();
    formData.append('picture', file);
    const response = await this.api.put('/auth/profile/picture', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteProfilePicture(): Promise<void> {
    await this.api.delete('/auth/profile/picture');
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.api.put('/auth/password', { currentPassword, newPassword });
  }

  async deleteAccount(): Promise<void> {
    await this.api.delete('/auth/me');
  }

  // Food endpoints
  async searchFoods(query: string): Promise<Food[]> {
    const response: AxiosResponse<ApiResponse<Food[]>> = 
      await this.api.get(`/food/search?q=${encodeURIComponent(query)}`);
    return response.data.data!;
  }

  async getFoodNutrition(foodId: string, quantity: number, unit: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = 
      await this.api.get(`/food/${foodId}/nutrition?quantity=${quantity}&unit=${unit}`);
    return response.data.data!;
  }

  async getFoodLogs(page = 1, limit = 10): Promise<PaginatedResponse<FoodLogEntry>> {
    const response: AxiosResponse<PaginatedResponse<FoodLogEntry>> = 
      await this.api.get(`/food/logs?page=${page}&limit=${limit}`);
    return response.data;
  }

  async getFoodLogByDate(date: string): Promise<FoodLogEntry | null> {
    // Check cache first
    const cacheKey = `food-log-${date}`;
    const cached = apiCache.get<FoodLogEntry | null>(cacheKey);
    if (cached !== null) {
      return cached;
    }

    try {
      const response: AxiosResponse<ApiResponse<FoodLogEntry>> = 
        await this.api.get(`/food/logs/${date}`);
      const data = response.data.data!;
      
      // Cache the result for 5 minutes
      apiCache.set(cacheKey, data, 5 * 60 * 1000);
      return data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        // Cache null result for 2 minutes (shorter TTL for 404s)
        apiCache.set(cacheKey, null, 2 * 60 * 1000);
        return null;
      }
      throw error;
    }
  }

  async getWeeklyFoodLogs(endDate: string): Promise<any[]> {
    const cacheKey = `weekly-food-logs-${endDate}`;
    const cached = apiCache.get<any[]>(cacheKey);
    if (cached !== null) return cached;
    const response = await this.api.get(`/food/logs/week?endDate=${endDate}`);
    const data = response.data?.data || [];
    apiCache.set(cacheKey, data, 5 * 60 * 1000);
    return data;
  }

  async createFoodLog(date: string, meals: any): Promise<FoodLogEntry> {
    const response: AxiosResponse<ApiResponse<FoodLogEntry>> = 
      await this.api.post('/food/logs', { date, meals });
    
    // Invalidate cache for this date
    const cacheKey = `food-log-${date}`;
    apiCache.set(cacheKey, response.data.data!, 5 * 60 * 1000);
    
    return response.data.data!;
  }

  async updateFoodLog(date: string, meals: any, mealPhotoUrls?: Record<string, string>): Promise<FoodLogEntry> {
    const response: AxiosResponse<ApiResponse<FoodLogEntry>> =
      await this.api.put(`/food/logs/${date}`, { meals, mealPhotoUrls });
    
    // Update cache with new data
    const cacheKey = `food-log-${date}`;
    apiCache.set(cacheKey, response.data.data!, 5 * 60 * 1000);
    
    return response.data.data!;
  }

  async deleteFoodLog(date: string): Promise<void> {
    await this.api.delete(`/food/logs/${date}`);
  }

  // Clear cache for a specific date
  clearFoodLogCache(date: string): void {
    apiCache.delete(`food-log-${date}`);
    apiCache.delete(`combined-nutrition-${date}`);
    apiCache.delete(`weekly-food-logs-${date}`);
  }

  async getNutritionSummary(days = 30): Promise<NutritionSummary> {
    const response: AxiosResponse<ApiResponse<NutritionSummary>> = 
      await this.api.get(`/food/summary?days=${days}`);
    return response.data.data!;
  }

  async getNutritionHistory(days = 7): Promise<{ date: string; calories: number; protein: number; carbs: number; fat: number }[]> {
    const response: AxiosResponse<any> = await this.api.get(`/food/nutrition-history?days=${days}`);
    return response.data.history || [];
  }

  async getCombinedNutrition(date: string): Promise<any> {
    const cacheKey = `combined-nutrition-${date}`;
    const cached = apiCache.get<any>(cacheKey);
    if (cached !== null) return cached;
    const response: AxiosResponse<any> =
      await this.api.get(`/food/combined-nutrition/${date}`);
    apiCache.set(cacheKey, response.data, 5 * 60 * 1000);
    return response.data;
  }

  // Bloodwork endpoints
  async uploadBloodworkPDF(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('pdf', file);

    const response: AxiosResponse<any> =
      await this.api.post('/bloodwork/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    return response.data;
  }

  async parseBloodworkPreview(file: File): Promise<any> {
    const formData = new FormData();
    formData.append('pdf', file);

    const response: AxiosResponse<any> =
      await this.api.post('/bloodwork/parse-preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    return response.data;
  }

  async confirmBloodwork(data: {
    labValues: LabValue[];
    testDate?: string;
    labName?: string;
    doctorName?: string;
    pdfUrl?: string;
    pdfPublicId?: string;
    extractionMethod?: string;
  }): Promise<any> {
    const response: AxiosResponse<any> =
      await this.api.post('/bloodwork/confirm', data);
    return response.data;
  }

  async createBloodworkEntry(date: string, labValues: LabValue[]): Promise<BloodworkEntry> {
    const response: AxiosResponse<ApiResponse<BloodworkEntry>> = 
      await this.api.post('/bloodwork/manual', { date, testDate: date, labValues });
    return response.data.data!;
  }

  async getBloodworkEntries(page = 1, limit = 10): Promise<PaginatedResponse<BloodworkEntry>> {
    const response: AxiosResponse<PaginatedResponse<BloodworkEntry>> = 
      await this.api.get(`/bloodwork?page=${page}&limit=${limit}`);
    return response.data;
  }

  async getBloodworkEntry(id: string): Promise<BloodworkEntry> {
    const response: AxiosResponse<ApiResponse<BloodworkEntry>> = 
      await this.api.get(`/bloodwork/${id}`);
    return response.data.data!;
  }

  async updateBloodworkEntry(id: string, labValues: LabValue[], metadata?: { testDate?: string; labName?: string; doctorName?: string }): Promise<BloodworkEntry> {
    const response: AxiosResponse<ApiResponse<BloodworkEntry>> =
      await this.api.put(`/bloodwork/${id}`, { labValues, ...metadata });
    return response.data.data!;
  }

  async deleteBloodworkEntry(id: string): Promise<void> {
    await this.api.delete(`/bloodwork/${id}`);
  }

  async getBloodworkTrends(category?: string): Promise<any> {
    const url = category ? `/bloodwork/trends?category=${category}` : '/bloodwork/trends';
    const response: AxiosResponse<ApiResponse<any>> = await this.api.get(url);
    return response.data.data!;
  }

  // Analysis endpoints
  async analyzeNutrition(days = 7, model?: string): Promise<Analysis> {
    const response: AxiosResponse<ApiResponse<Analysis>> = 
      await this.api.post('/analysis/nutrition', { days, model });
    return response.data.data!;
  }

  async analyzeBloodwork(model?: string, bloodworkId?: string): Promise<Analysis> {
    const response: AxiosResponse<ApiResponse<Analysis>> = 
      await this.api.post('/analysis/bloodwork', { bloodworkId, model });
    return response.data.data!;
  }

  async analyzeCorrelation(nutritionDays = 30, model?: string, bloodworkId?: string): Promise<Analysis> {
    const response: AxiosResponse<ApiResponse<Analysis>> = 
      await this.api.post('/analysis/correlation', { nutritionDays, bloodworkId, model });
    return response.data.data!;
  }

  async getAnalyses(page = 1, limit = 10, type?: string): Promise<PaginatedResponse<Analysis>> {
    const url = type ? `/analysis?page=${page}&limit=${limit}&type=${type}` : `/analysis?page=${page}&limit=${limit}`;
    const response: AxiosResponse<PaginatedResponse<Analysis>> = await this.api.get(url);
    return response.data;
  }

  async getAnalysis(id: string): Promise<Analysis> {
    const response: AxiosResponse<ApiResponse<Analysis>> = await this.api.get(`/analysis/${id}`);
    return response.data.data!;
  }

  async deleteAnalysis(id: string): Promise<void> {
    await this.api.delete(`/analysis/${id}`);
  }

  async getSecondOpinion(analysisId: string, secondOpinionModel: string): Promise<Analysis> {
    const response: AxiosResponse<ApiResponse<Analysis>> = 
      await this.api.patch(`/analysis/${analysisId}/second-opinion`, { secondOpinionModel });
    return response.data.data!;
  }

  // Development only - delete test users
  async deleteTestUser(email: string): Promise<{ message: string; deletedCount: number }> {
    const response = await this.api.delete(`/auth/delete-test-user/${email}`);
    return response.data; // The backend returns the data directly, not wrapped
  }

  async deleteAllTestUsers(): Promise<{ message: string; deletedCount: number }> {
    const response = await this.api.delete('/auth/delete-all-test-users');
    return response.data; // The backend returns the data directly, not wrapped
  }

  async forgotPassword(email: string): Promise<any> {
    const response = await this.api.post('/auth/forgot-password', { email });
    return response.data;
  }

  async resetPassword(token: string, password: string): Promise<any> {
    const response = await this.api.post('/auth/reset-password', { token, password });
    return response.data;
  }

  async resendVerification(email: string): Promise<any> {
    const response = await this.api.post('/auth/resend-verification', { email });
    return response.data;
  }

  // Get analysis models
  async getAnalysisModels(): Promise<{ models: { value: string; label: string; description: string }[]; currentModel: string }> {
    const response: AxiosResponse<ApiResponse<{ models: { value: string; label: string; description: string }[]; currentModel: string }>> = 
      await this.api.get('/analysis/models');
    return response.data.data!;
  }

  // Supplement endpoints
  async analyzeSupplementWithAI(query: string): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = 
      await this.api.post('/supplements/analyze', { query });
    return response.data.data!;
  }

  async logSupplement(data: {
    date: string;
    timeOfDay: string;
    supplements: any[];
    notes?: string;
  }): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = 
      await this.api.post('/supplements/log', data);
    return response.data.data!;
  }

  async getSupplementsForDate(date: string): Promise<any[]> {
    const response: AxiosResponse<ApiResponse<any[]>> = 
      await this.api.get(`/supplements/date/${date}`);
    return response.data.data!;
  }

  async deleteSupplementLog(logId: string): Promise<void> {
    await this.api.delete(`/supplements/logs/${logId}`);
  }

  async getSupplementSummary(days = 30): Promise<any> {
    const response: AxiosResponse<ApiResponse<any>> = 
      await this.api.get(`/supplements/summary?days=${days}`);
    return response.data.data!;
  }

  async getMe(): Promise<{ user: User }> {
    const response: AxiosResponse<{ user: User }> = await this.api.get('/auth/me');
    return response.data;
  }

  // Payments and Billing
  async getPaymentMethods(): Promise<any> {
    const response = await this.api.get('/payment-methods');
    return response.data;
  }

  async addCreditCard(data: { sourceId: string; nickname?: string; billingAddress?: any }): Promise<any> {
    const response = await this.api.post('/payment-methods/card', data);
    return response.data;
  }

  async addPayPal(data: { email: string; nickname?: string }): Promise<any> {
    const response = await this.api.post('/payment-methods/paypal', data);
    return response.data;
  }

  async updatePaymentMethod(id: string, data: any): Promise<any> {
    const response = await this.api.put(`/payment-methods/${id}`, data);
    return response.data;
  }

  async setDefaultPaymentMethod(id: string): Promise<any> {
    const response = await this.api.put(`/payment-methods/${id}/default`);
    return response.data;
  }

  async deletePaymentMethod(id: string): Promise<any> {
    const response = await this.api.delete(`/payment-methods/${id}`);
    return response.data;
  }

  async updateBillingAddress(data: { billingAddress: any; phone?: string }): Promise<any> {
    const response = await this.api.put('/payment-methods/billing-address', data);
    return response.data;
  }

  async getSubscriptionDetails(): Promise<any> {
    const response = await this.api.get('/payments/subscription/status');
    return response.data;
  }

  async getBillingHistory(): Promise<any> {
    const response = await this.api.get('/payments/billing-history');
    return response.data;
  }

  async cancelSubscription(): Promise<any> {
    const response = await this.api.post('/payments/cancel-subscription');
    return response.data;
  }

  async getSubscriptionStatus(): Promise<any> {
    const response = await this.api.get('/payments/subscription/status');
    return response.data;
  }

  async pauseSubscription(): Promise<any> {
    const response = await this.api.post('/payments/pause-subscription');
    return response.data;
  }

  async updateSubscriptionPaymentMethod(paymentMethodId: string): Promise<any> {
    const response = await this.api.put('/payments/update-payment-method', { paymentMethodId });
    return response.data;
  }

  async processPayment(sourceId: string): Promise<any> {
    const response = await this.api.post('/payments/process', { sourceId });
    return response.data;
  }

  // Generic, authenticated request methods
  async get(endpoint: string, config?: any): Promise<any> {
    return this.api.get(endpoint, config);
  }

  async post(endpoint: string, data: any, config?: any): Promise<any> {
    return this.api.post(endpoint, data, config);
  }

  async put(endpoint: string, data: any, config?: any): Promise<any> {
    return this.api.put(endpoint, data, config);
  }

  async delete(endpoint: string, config?: any): Promise<any> {
    return this.api.delete(endpoint, config);
  }

  // AI Content Reporting
  async submitAIContentReport(data: {
    reason: string;
    description?: string;
    contentType: 'ai_assistant' | 'meal_suggestion' | 'health_insight' | 'recipe' | 'analysis';
    content: string;
  }): Promise<{ message: string; reportId: string }> {
    try {
      const response = await this.api.post('/ai-content-reports', data);
      return response.data;
    } catch (error) {
      console.error('Error submitting AI content report:', error);
      throw error;
    }
  }

  async getMyAIContentReports(): Promise<{ reports: any[] }> {
    try {
      const response = await this.api.get('/ai-content-reports/my-reports');
      return response.data;
    } catch (error) {
      console.error('Error fetching AI content reports:', error);
      throw error;
    }
  }

  async getAllAIContentReports(): Promise<{ reports: any[] }> {
    try {
      const response = await this.api.get('/ai-content-reports/admin/all');
      return response.data;
    } catch (error) {
      console.error('Error fetching all AI content reports:', error);
      throw error;
    }
  }

  async updateAIContentReportStatus(
    reportId: string, 
    status: 'pending' | 'reviewed' | 'resolved' | 'dismissed',
    adminNotes?: string
  ): Promise<{ message: string; report: any }> {
    try {
      const response = await this.api.patch(`/ai-content-reports/admin/${reportId}`, {
        status,
        adminNotes
      });
      return response.data;
    } catch (error) {
      console.error('Error updating AI content report status:', error);
      throw error;
    }
  }

  // ===== ADMIN API METHODS =====

  // User Management
  async getUsers(page = 1, limit = 20, search = '', tier = '', role = ''): Promise<any> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && { search }),
        ...(tier && { tier }),
        ...(role && { role })
      });
      const response = await this.api.get(`/admin/users?${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  async upgradeUser(userId: string, tier = 'pro', subscriptionStatus = 'active', adminNotes = ''): Promise<any> {
    try {
      const response = await this.api.patch(`/admin/users/${userId}/upgrade`, {
        tier,
        subscriptionStatus,
        adminNotes
      });
      return response.data;
    } catch (error) {
      console.error('Error upgrading user:', error);
      throw error;
    }
  }

  async getUserDetails(userId: string): Promise<any> {
    try {
      const response = await this.api.get(`/admin/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user details:', error);
      throw error;
    }
  }

  // Analytics
  async getNutritionAnalytics(days = 30): Promise<any> {
    try {
      const response = await this.api.get(`/admin/analytics/nutrition?days=${days}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching nutrition analytics:', error);
      throw error;
    }
  }

  async getBloodworkAnalytics(days = 90): Promise<any> {
    try {
      const response = await this.api.get(`/admin/analytics/bloodwork?days=${days}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching bloodwork analytics:', error);
      throw error;
    }
  }

  async getFuelIQAnalytics(days = 30): Promise<any> {
    try {
      const response = await this.api.get(`/admin/analytics/fuel-iq?days=${days}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching Fuel IQ analytics:', error);
      throw error;
    }
  }

  async getEngagementAnalytics(days = 30): Promise<any> {
    try {
      const response = await this.api.get(`/admin/analytics/engagement?days=${days}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching engagement analytics:', error);
      throw error;
    }
  }

  async getDashboardStats(): Promise<any> {
    const response = await this.api.get('/admin/analytics/dashboard');
    return response.data;
  }

  async getRevenueAnalytics(): Promise<any> {
    const response = await this.api.get('/admin/analytics/revenue');
    return response.data;
  }

  async getFeatureAdoptionAnalytics(): Promise<any> {
    const response = await this.api.get('/admin/analytics/feature-adoption');
    return response.data;
  }

  async getSocialFeedAnalytics(days = 30): Promise<any> {
    try {
      const response = await this.api.get(`/admin/analytics/social-feed?days=${days}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching social feed analytics:', error);
      throw error;
    }
  }

  async getCurrentInsight(): Promise<any> {
    const response = await this.api.get('/insights/current');
    return response.data;
  }

  async markInsightSeen(): Promise<void> {
    await this.api.post('/insights/current/seen', {});
  }

  /** "Don't show this again": mutes the pattern for 90 days and records a decision memory. */
  async muteInsight(key: string, title?: string): Promise<void> {
    await this.api.post('/insights/current/mute', { key, title });
  }

  async getUsageAnalytics(days = 30): Promise<any> {
    const response = await this.api.get(`/admin/analytics/usage?days=${days}`);
    return response.data;
  }

  async getOnboardingAnalytics(days = 90): Promise<any> {
    const response = await this.api.get(`/admin/analytics/onboarding?days=${days}`);
    return response.data;
  }

  async getUserDiagnostics(userId: string): Promise<any> {
    const response = await this.api.get(`/admin/users/${userId}/diagnostics`);
    return response.data;
  }

  async getFuelTimeline(date: string): Promise<any> {
    const response = await this.api.get(`/food/fuel-timeline/${date}`);
    return response.data?.data || { date, meals: [], energyCheckins: [] };
  }

  async logEnergyCheckin(date: string, level: number, note?: string): Promise<any> {
    const response = await this.api.post('/food/energy-checkin', { date, level, note });
    return response.data?.data || null;
  }

  async getMicronutrientConsistency(days = 7): Promise<any> {
    const response = await this.api.get(`/food/micronutrient-consistency?days=${days}`);
    return response.data?.data || { days: [], nutrients: [], hasSupplements: false };
  }

  async getFiberSatFatTrend(days = 14): Promise<any> {
    const response = await this.api.get(`/food/fiber-satfat-trend?days=${days}`);
    return response.data?.data || { days: [], trend: [], fiberGoal: 38 };
  }

  async getSupplementGapClosure(): Promise<any> {
    const response = await this.api.get('/supplements-new/gap-closure');
    return response.data?.data || { nutrients: [], hasSupplements: false };
  }

  async getRecoveryFuel(days = 7): Promise<any> {
    const response = await this.api.get(`/food/recovery-fuel?days=${days}`);
    return response.data?.data || { days: [], rows: [], hasRecovery: false, hasFuel: false, hasActivity: false };
  }

  async getSleepVsLateNight(days = 10): Promise<any> {
    const response = await this.api.get(`/food/sleep-vs-latenight?days=${days}`);
    return response.data?.data || { rows: [], hasSleep: false };
  }

  async getHeartNutrition(date: string): Promise<any> {
    const response = await this.api.get(`/food/heart-nutrition/${date}`);
    return response.data?.data || null;
  }

  /**
   * Top movers for the Analysis "What changed" strip. Throws on any transport
   * or shape problem (404/500, malformed body) — the strip renders nothing on
   * failure, so callers must catch.
   */
  async getWhatChanged(days: 7 | 30 | 90): Promise<WhatChangedData> {
    const response = await this.api.get(`/analysis/what-changed?days=${days}`);
    const payload = response.data?.data;
    if (!response.data?.success || !payload || typeof payload !== 'object') {
      throw new Error('what-changed: malformed response');
    }
    return {
      ...payload,
      days: Number(payload.days) || days,
      items: Array.isArray(payload.items) ? payload.items : [],
      skipped: Array.isArray(payload.skipped) ? payload.skipped : [],
    };
  }

  async getHealthKitHRVHistory(days = 7): Promise<{ history: Array<{ date: string; hrv: number }>; days: number }> {
    const response = await this.api.get(`/healthkit/hrv-history?days=${days}`);
    return response.data?.data || { history: [], days };
  }

  async getHealthKitCalorieBalance(days = 7): Promise<{ days: Array<{ date: string; consumed: number; burned: number }> }> {
    const response = await this.api.get(`/healthkit/calorie-balance?days=${days}`);
    return response.data?.data || { days: [] };
  }

  async getWeightHistory(days = 30): Promise<any> {
    const response = await this.api.get(`/weight/history?days=${days}`);
    return response.data?.data || null;
  }

  // ── Long-range trends (Analysis page, 2026-10-09) ──
  /** Save the FuelTron score the Dashboard shows, one snapshot per local day (last write wins). */
  async saveScoreSnapshot(snapshot: { date: string; overall: number; nutrition: number; health?: number | null; activity?: number | null; recovery?: number | null; bloodwork?: number | null; build?: number | null }): Promise<void> {
    await this.api.post('/trends/scores', snapshot);
  }

  async getScoreHistory(days = 30): Promise<{ days: string[]; scores: Array<{ date: string; overall: number; nutrition: number; health: number | null; activity: number | null; recovery: number | null; bloodwork: number | null; build: number | null }> }> {
    const response = await this.api.get('/trends/scores', { params: { days } });
    return response.data?.data || { days: [], scores: [] };
  }

  /** Minutes of activity per local day by kind, plus WHO-equivalent minutes. */
  async getActivityTrend(days = 30): Promise<Array<{ date: string; cardio: number; strength: number; flexibility: number; who: number; sessions: number }>> {
    const response = await this.api.get('/trends/activity', { params: { days } });
    return response.data?.data || [];
  }

  /** Every lab marker's readings, with linked supplement start dates. */
  async getLabTrends(): Promise<{ markers: Array<{ key: string; name: string; unit: string; readings: Array<{ date: string; value: number; status?: string; referenceRange?: string }>; supplements: Array<{ name: string; startDate: string; active: boolean }> }> }> {
    const response = await this.api.get('/trends/labs');
    return response.data?.data || { markers: [] };
  }

  async getStrengthHistory(days = 365): Promise<Array<{ _id: string; date: string; sets: Array<{ exercise: string; reps: number; weight: number; weightUnit: 'lbs' | 'kg' }> }>> {
    const response = await this.api.get('/exercise/strength-history', { params: { days } });
    return response.data?.data || [];
  }

  async getBodyCompositionHistory(days = 365): Promise<Array<{ measuredAt: string; source: string; weightGrams?: number; bodyFatPercent?: number; leanMassGrams?: number; fatMassGrams?: number }>> {
    const response = await this.api.get('/body-composition/history', { params: { days } });
    return response.data?.data?.entries || [];
  }

  /** Ask Apple to post a TEST notification to our Server Notifications URL. */
  async requestAppleTestNotification(): Promise<{ testNotificationToken: string }> {
    const response = await this.api.post('/admin/iap/apple/test-notification');
    return response.data?.data;
  }

  /** Read what Apple recorded when it delivered that test notification. */
  async getAppleTestNotificationStatus(token: string): Promise<any> {
    const response = await this.api.get(`/admin/iap/apple/test-notification/${encodeURIComponent(token)}`);
    return response.data?.data;
  }

  async getAuditLog(page = 1, limit = 50, action?: string): Promise<any> {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (action) params.set('action', action);
    const response = await this.api.get(`/admin/audit-log?${params}`);
    return response.data;
  }

  async getSupplementCorrelations(): Promise<any> {
    const response = await this.api.get('/bloodwork/correlations');
    return response.data;
  }

  async exportUserData(): Promise<any> {
    const response = await this.api.get('/auth/export-data');
    return response.data;
  }

  // Google OAuth (supports both ID token and access token flows)
  async loginWithGoogle(tokenOrCredential: string): Promise<{ token: string; user: User }> {
    const response = await this.api.post('/auth/google', { access_token: tokenOrCredential });
    return response.data;
  }

  // Apple Sign-In
  async loginWithApple(data: { identityToken: string; fullName?: { givenName?: string; familyName?: string }; email?: string; authorizationCode?: string }): Promise<{ token: string; user: User }> {
    const response = await this.api.post('/auth/apple', data);
    return response.data;
  }

  // Meals and Individual Foods API methods
  async getMeals(options: {
    search?: string;
    category?: string;
    favorites?: boolean;
    sort?: string;
    type?: 'all' | 'meals' | 'foods';
    limit?: number;
    page?: number;
  } = {}): Promise<any> {
    const params = new URLSearchParams();
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const response = await this.api.get(`/meals?${params.toString()}`);
    return response.data;
  }

  async getRecentFrequentFoods(limit: number = 10): Promise<any> {
    const response = await this.api.get(`/meals/recent-frequent?limit=${limit}`);
    return response.data;
  }

  async getMeal(id: string): Promise<any> {
    const response = await this.api.get(`/meals/${id}`);
    return response.data;
  }

  async createMeal(data: {
    name: string;
    description?: string;
    category?: string;
    foods: any[];
    photoUrl?: string;
    notes?: string;
  }): Promise<any> {
    const response = await this.api.post('/meals', data);
    return response.data;
  }

  async updateMeal(id: string, data: any): Promise<any> {
    const response = await this.api.put(`/meals/${id}`, data);
    return response.data;
  }

  async deleteMeal(id: string): Promise<any> {
    const response = await this.api.delete(`/meals/${id}`);
    return response.data;
  }

  async toggleMealFavorite(id: string): Promise<any> {
    const response = await this.api.patch(`/meals/${id}/favorite`);
    return response.data;
  }

  async logMeal(id: string, data: {
    date: string;
    mealType: string;
    quantity?: number;
  }): Promise<any> {
    const response = await this.api.post(`/meals/${id}/log`, data);
    return response.data;
  }

  async createMealFromFoodLog(data: {
    name: string;
    description?: string;
    category?: string;
    date: string;
    mealType: string;
    notes?: string;
  }): Promise<any> {
    const response = await this.api.post('/meals/from-food-log', data);
    return response.data;
  }

  // Individual Foods API methods
  async getIndividualFoods(options: {
    search?: string;
    category?: string;
    favorites?: boolean;
    sort?: string;
    limit?: number;
    page?: number;
  } = {}): Promise<any> {
    const params = new URLSearchParams();
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const response = await this.api.get(`/individual-foods?${params.toString()}`);
    return response.data;
  }

  async getIndividualFood(id: string): Promise<any> {
    const response = await this.api.get(`/individual-foods/${id}`);
    return response.data;
  }

  async createIndividualFood(data: {
    name: string;
    quantity: number;
    unit: string;
    nutrition: any;
    category?: string;
    notes?: string;
  }): Promise<any> {
    const response = await this.api.post('/individual-foods', data);
    return response.data;
  }

  async updateIndividualFood(id: string, data: any): Promise<any> {
    const response = await this.api.put(`/individual-foods/${id}`, data);
    return response.data;
  }

  async deleteIndividualFood(id: string): Promise<any> {
    const response = await this.api.delete(`/individual-foods/${id}`);
    return response.data;
  }

  async toggleIndividualFoodFavorite(id: string): Promise<any> {
    const response = await this.api.patch(`/individual-foods/${id}/favorite`);
    return response.data;
  }

  async logIndividualFood(id: string, data: {
    date: string;
    mealType: string;
    quantity?: number;
  }): Promise<any> {
    const response = await this.api.post(`/individual-foods/${id}/log`, data);
    return response.data;
  }

  async createIndividualFoodFromFoodLog(data: {
    name: string;
    quantity: number;
    unit: string;
    nutrition: any;
    category?: string;
    notes?: string;
  }): Promise<any> {
    const response = await this.api.post('/individual-foods/from-food-log', data);
    return response.data;
  }

  // Notification methods
  async registerPushToken(token: string): Promise<any> {
    const response = await this.api.post('/notifications/register-token', { token });
    return response.data;
  }

  async unregisterPushToken(): Promise<any> {
    const response = await this.api.delete('/notifications/unregister-token');
    return response.data;
  }

  async getNotificationHistory(limit: number = 50, unreadOnly: boolean = false): Promise<any> {
    const response = await this.api.get('/notifications/history', {
      params: { limit, unreadOnly }
    });
    return response.data;
  }

  async getUnreadNotificationCount(): Promise<any> {
    const response = await this.api.get('/notifications/unread-count');
    return response.data;
  }

  async markNotificationAsRead(notificationId: string): Promise<any> {
    const response = await this.api.put(`/notifications/${notificationId}/read`);
    return response.data;
  }

  async markAllNotificationsAsRead(): Promise<any> {
    const response = await this.api.put('/notifications/mark-all-read');
    return response.data;
  }

  async sendTestNotification(): Promise<any> {
    const response = await this.api.post('/notifications/test');
    return response.data;
  }

  async deleteNotification(notificationId: string): Promise<any> {
    const response = await this.api.delete(`/notifications/${notificationId}`);
    return response.data;
  }

  async clearAllNotifications(): Promise<any> {
    const response = await this.api.delete('/notifications/clear-all');
    return response.data;
  }

  // ===== GARMIN API METHODS =====

  async getGarminStatus(): Promise<any> {
    const response = await this.api.get('/garmin/status');
    return response.data.data;
  }

  async connectGarmin(): Promise<{ authUrl: string }> {
    const response = await this.api.get('/garmin/connect');
    return response.data.data;
  }

  async disconnectGarmin(): Promise<any> {
    const response = await this.api.post('/garmin/disconnect');
    return response.data;
  }

  async syncGarmin(): Promise<any> {
    const response = await this.api.post('/garmin/sync');
    return response.data;
  }

  async getGarminSyncStatus(syncId: string): Promise<any> {
    const response = await this.api.get(`/garmin/sync/${syncId}/status`);
    return response.data.data;
  }

  async getGarminDailySummary(date: string): Promise<any> {
    const response = await this.api.get(`/garmin/daily/${date}`);
    return response.data.data;
  }

  async getGarminDailySummaries(start?: string, end?: string): Promise<any[]> {
    const params = new URLSearchParams();
    if (start) params.append('start', start);
    if (end) params.append('end', end);
    const response = await this.api.get(`/garmin/daily?${params.toString()}`);
    return response.data.data;
  }

  async getGarminSleep(date: string): Promise<any> {
    const response = await this.api.get(`/garmin/sleep/${date}`);
    return response.data.data;
  }

  async getGarminActivities(start?: string, end?: string, limit?: number): Promise<any[]> {
    const params = new URLSearchParams();
    if (start) params.append('start', start);
    if (end) params.append('end', end);
    if (limit) params.append('limit', limit.toString());
    const response = await this.api.get(`/garmin/activities?${params.toString()}`);
    return response.data.data;
  }

  async getGarminEpochs(date: string): Promise<any[]> {
    const response = await this.api.get(`/garmin/epochs/${date}`);
    return response.data.data;
  }

  async getGarminStress(date: string): Promise<any> {
    const response = await this.api.get(`/garmin/stress/${date}`);
    return response.data.data;
  }

  async getGarminHRV(date: string): Promise<any> {
    const response = await this.api.get(`/garmin/hrv/${date}`);
    return response.data.data;
  }

  async getGarminBodyComposition(): Promise<any> {
    const response = await this.api.get('/garmin/body-composition');
    return response.data.data;
  }

  async getGarminCorrelations(days = 7): Promise<any> {
    const response = await this.api.get(`/garmin/correlations?days=${days}`);
    return response.data.data;
  }

  async getGarminCorrelation(type: string, days = 7): Promise<any> {
    const response = await this.api.get(`/garmin/correlations/${type}?days=${days}`);
    return response.data.data;
  }

  async getGarminHRVHistory(days = 7): Promise<{ history: Array<{ date: string; hrv: number; status?: string; baselineLow?: number; baselineHigh?: number }>; days: number }> {
    const response = await this.api.get(`/garmin/hrv-history?days=${days}`);
    return response.data.data;
  }

  // ===== HEALTHKIT API METHODS =====

  async connectHealthKit(): Promise<any> {
    const response = await this.api.post('/healthkit/connect');
    return response.data;
  }

  async disconnectHealthKit(): Promise<any> {
    const response = await this.api.post('/healthkit/disconnect');
    return response.data;
  }

  async getHealthKitStatus(): Promise<any> {
    const response = await this.api.get('/healthkit/status');
    return response.data.data;
  }

  async syncHealthKit(data: any): Promise<any> {
    const response = await this.api.post('/healthkit/sync', data);
    return response.data;
  }

  async getHealthKitDailySummary(date: string): Promise<any> {
    const response = await this.api.get(`/healthkit/daily/${date}`);
    return response.data.data;
  }

  async getHealthKitSleep(date: string): Promise<any> {
    const response = await this.api.get(`/healthkit/sleep/${date}`);
    return response.data.data;
  }

  async getHealthKitHRV(date: string): Promise<any> {
    const response = await this.api.get(`/healthkit/hrv/${date}`);
    return response.data.data;
  }

  // ===== FEED API METHODS =====

  async createPost(formData: FormData): Promise<any> {
    const response = await this.api.post('/feed/posts', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async getFeed(cursor?: string, limit = 20): Promise<any> {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (cursor) params.append('cursor', cursor);
    const response = await this.api.get(`/feed?${params.toString()}`);
    return response.data;
  }

  async getMyPosts(cursor?: string, limit = 20): Promise<any> {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (cursor) params.append('cursor', cursor);
    const response = await this.api.get(`/feed/my-posts?${params.toString()}`);
    return response.data;
  }

  async deletePost(postId: string): Promise<any> {
    const response = await this.api.delete(`/feed/posts/${postId}`);
    return response.data;
  }

  async toggleLike(postId: string): Promise<any> {
    const response = await this.api.post(`/feed/posts/${postId}/like`);
    return response.data;
  }

  async addComment(postId: string, data: FormData): Promise<any> {
    const response = await this.api.post(`/feed/posts/${postId}/comments`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async deleteComment(postId: string, commentId: string): Promise<any> {
    const response = await this.api.delete(`/feed/posts/${postId}/comments/${commentId}`);
    return response.data;
  }

  async toggleDislike(postId: string): Promise<any> {
    const response = await this.api.post(`/feed/posts/${postId}/dislike`);
    return response.data;
  }

  async toggleCommentLike(postId: string, commentId: string): Promise<any> {
    const response = await this.api.post(`/feed/posts/${postId}/comments/${commentId}/like`);
    return response.data;
  }

  async toggleCommentDislike(postId: string, commentId: string): Promise<any> {
    const response = await this.api.post(`/feed/posts/${postId}/comments/${commentId}/dislike`);
    return response.data;
  }

  async copyMealFromPost(postId: string, date: string, mealType: string): Promise<any> {
    const response = await this.api.post(`/feed/posts/${postId}/copy-meal`, { date, mealType });
    return response.data;
  }

  // ===== CONTENT REPORTING =====

  async reportContent(data: {
    contentType: 'post' | 'comment';
    contentId: string;
    contentAuthorId: string;
    reason: 'inappropriate' | 'spam' | 'harassment' | 'misinformation' | 'other';
    description?: string;
  }): Promise<any> {
    const response = await this.api.post('/social/report', data);
    return response.data;
  }

  async getContentReports(): Promise<any> {
    const response = await this.api.get('/social/reports');
    return response.data;
  }

  async updateContentReportStatus(reportId: string, status: string, adminNotes?: string): Promise<any> {
    const response = await this.api.patch(`/social/reports/${reportId}`, { status, adminNotes });
    return response.data;
  }
}

export const apiService = new ApiService();

export const auth = {
  register: (data: RegisterData) => 
    apiService.register(data),
  
  login: (data: LoginData) => 
    apiService.login(data),
  
  getProfile: () => 
    apiService.getProfile(),
  
  updateProfile: (data: Partial<any>) => 
    apiService.updateProfile(data),
  
  changePassword: (data: { currentPassword: string; newPassword: string }) => 
    apiService.changePassword(data.currentPassword, data.newPassword),
  
  deleteAccount: () => 
    apiService.deleteAccount(),
  
  // Development only - delete test users
  deleteTestUser: (email: string) => 
    apiService.deleteTestUser(email),
  
  deleteAllTestUsers: () =>
    apiService.deleteAllTestUsers(),

  resendVerification: (email: string) =>
    apiService.resendVerification(email),

  forgotPassword: (email: string) =>
    apiService.forgotPassword(email),

  resetPassword: (token: string, password: string) =>
    apiService.resetPassword(token, password),
};

export const food = {
  searchFoods: (query: string) => 
    apiService.searchFoods(query),
  
  getFoodNutrition: (foodId: string, quantity: number, unit: string) => 
    apiService.getFoodNutrition(foodId, quantity, unit),
  
  getFoodLogs: (page = 1, limit = 10) => 
    apiService.getFoodLogs(page, limit),
  
  getFoodLogByDate: (date: string) => 
    apiService.getFoodLogByDate(date),
  
  createFoodLog: (date: string, meals: any) => 
    apiService.createFoodLog(date, meals),
  
  updateFoodLog: (date: string, meals: any, mealPhotoUrls?: Record<string, string>) =>
    apiService.updateFoodLog(date, meals, mealPhotoUrls),
  
  deleteFoodLog: (date: string) => 
    apiService.deleteFoodLog(date),
  
  getNutritionSummary: (days = 30) => 
    apiService.getNutritionSummary(days),
  
  getCombinedNutrition: (date: string) => 
    apiService.getCombinedNutrition(date),
};

export const bloodwork = {
  uploadBloodworkPDF: (file: File) => 
    apiService.uploadBloodworkPDF(file),
  
  createBloodworkEntry: (date: string, labValues: LabValue[]) => 
    apiService.createBloodworkEntry(date, labValues),
  
  getBloodworkEntries: (page = 1, limit = 10) => 
    apiService.getBloodworkEntries(page, limit),
  
  getBloodworkEntry: (id: string) => 
    apiService.getBloodworkEntry(id),
  
  updateBloodworkEntry: (id: string, labValues: LabValue[]) => 
    apiService.updateBloodworkEntry(id, labValues),
  
  deleteBloodworkEntry: (id: string) =>
    apiService.deleteBloodworkEntry(id),

  getBloodworkTrends: (category?: string) => 
    apiService.getBloodworkTrends(category),
};

export const analysis = {
  analyzeNutrition: (days = 7, model?: string) => 
    apiService.analyzeNutrition(days, model),
  
  analyzeBloodwork: (model?: string, bloodworkId?: string) => 
    apiService.analyzeBloodwork(model, bloodworkId),
  
  analyzeCorrelation: (nutritionDays = 30, model?: string, bloodworkId?: string) => 
    apiService.analyzeCorrelation(nutritionDays, model, bloodworkId),
  
  getAnalyses: (page = 1, limit = 10, type?: string) => 
    apiService.getAnalyses(page, limit, type),
  
  getAnalysis: (id: string) => 
    apiService.getAnalysis(id),
  
  deleteAnalysis: (id: string) => 
    apiService.deleteAnalysis(id),

  // Get analysis models
  getAnalysisModels: () => apiService.getAnalysisModels(),

  getSecondOpinion: (analysisId: string, secondOpinionModel: string) => 
    apiService.getSecondOpinion(analysisId, secondOpinionModel),
};

export const supplements = {
  analyzeSupplementWithAI: (query: string) => 
    apiService.analyzeSupplementWithAI(query),
  
  logSupplement: (data: {
    date: string;
    timeOfDay: string;
    supplements: any[];
    notes?: string;
  }) => 
    apiService.logSupplement(data),
  
  getSupplementsForDate: (date: string) => 
    apiService.getSupplementsForDate(date),
  
  deleteSupplementLog: (logId: string) => 
    apiService.deleteSupplementLog(logId),
  
  getSupplementSummary: (days = 30) => 
    apiService.getSupplementSummary(days),
};

export const payments = {
  getPaymentMethods: () => 
    apiService.getPaymentMethods(),
  
  addCreditCard: (data: any) => 
    apiService.addCreditCard(data),
  
  addPayPal: (data: any) => 
    apiService.addPayPal(data),
  
  updatePaymentMethod: (id: string, data: any) => 
    apiService.updatePaymentMethod(id, data),
  
  setDefaultPaymentMethod: (id: string) => 
    apiService.setDefaultPaymentMethod(id),
  
  deletePaymentMethod: (id: string) => 
    apiService.deletePaymentMethod(id),
  
  updateBillingAddress: (data: any) => 
    apiService.updateBillingAddress(data),
  
  getSubscriptionDetails: () => 
    apiService.getSubscriptionDetails(),
  
  getBillingHistory: () => 
    apiService.getBillingHistory(),
  
  cancelSubscription: () => 
    apiService.cancelSubscription(),
  
  
  getSubscriptionStatus: () => 
    apiService.getSubscriptionStatus(),
  
  pauseSubscription: () => 
    apiService.pauseSubscription(),
  
  updateSubscriptionPaymentMethod: (paymentMethodId: string) => 
    apiService.updateSubscriptionPaymentMethod(paymentMethodId),
  
  processPayment: (sourceId: string) => 
    apiService.processPayment(sourceId),
};

export const garmin = {
  getStatus: () => apiService.getGarminStatus(),
  connect: () => apiService.connectGarmin(),
  disconnect: () => apiService.disconnectGarmin(),
  sync: () => apiService.syncGarmin(),
  getSyncStatus: (syncId: string) => apiService.getGarminSyncStatus(syncId),
  getDailySummary: (date: string) => apiService.getGarminDailySummary(date),
  getDailySummaries: (start?: string, end?: string) => apiService.getGarminDailySummaries(start, end),
  getSleep: (date: string) => apiService.getGarminSleep(date),
  getActivities: (start?: string, end?: string, limit?: number) => apiService.getGarminActivities(start, end, limit),
  getEpochs: (date: string) => apiService.getGarminEpochs(date),
  getStress: (date: string) => apiService.getGarminStress(date),
  getHRV: (date: string) => apiService.getGarminHRV(date),
  getBodyComposition: () => apiService.getGarminBodyComposition(),
  getCorrelations: (days?: number) => apiService.getGarminCorrelations(days),
  getCorrelation: (type: string, days?: number) => apiService.getGarminCorrelation(type, days),
};

export const feed = {
  createPost: (formData: FormData) => apiService.createPost(formData),
  getFeed: (cursor?: string, limit?: number) => apiService.getFeed(cursor, limit),
  getMyPosts: (cursor?: string, limit?: number) => apiService.getMyPosts(cursor, limit),
  deletePost: (postId: string) => apiService.deletePost(postId),
  toggleLike: (postId: string) => apiService.toggleLike(postId),
  toggleDislike: (postId: string) => apiService.toggleDislike(postId),
  addComment: (postId: string, data: FormData) => apiService.addComment(postId, data),
  deleteComment: (postId: string, commentId: string) => apiService.deleteComment(postId, commentId),
  toggleCommentLike: (postId: string, commentId: string) => apiService.toggleCommentLike(postId, commentId),
  toggleCommentDislike: (postId: string, commentId: string) => apiService.toggleCommentDislike(postId, commentId),
  copyMealFromPost: (postId: string, date: string, mealType: string) => apiService.copyMealFromPost(postId, date, mealType),
};

export default apiService;
